import React, { useMemo } from 'react';
import ExpandableWrapper from '../ExpandableWrapper';
import { useIntl } from 'react-intl';
import {
  Risk, RefTag, Text, Line, Title, Link, RiskScoreInfo, RiskScore,
  IconButton, RefreshIcon, ButtonsWrapper
} from './styled';
import { useDispatch, useSelector } from 'react-redux';
import useExpandable from 'Hooks/useExpandable';
import CVE from '../CVE';
import { selectRiskScoreFeatureFlag } from 'store/Faraday/selectors';
import CVSS from '../CVSS';
import SpinnerComponent from 'Common/Components/SpinnerContainer';
import CWE from '../CWE';
import { selectAllowedByRole } from 'Hooks/usePermission/selector';
import { FEATURE_VULNS, PERMISSION_TYPES } from 'Hooks/usePermission/permissions';

const TAB = 'general';
const ID = 'risk';

const RiskAndCVE = ({
  currentVuln, updateVuln, scrollToReferences, tempCVSS, resetCVSS, refreshVulnEnrichment, entity,
  fetchingVulnEnrichment
}) => {
  const intl = useIntl();
  const isExpanded = useExpandable(TAB, ID, currentVuln.cve, currentVuln?._id);
  const dispatch = useDispatch();
  const isAllowedByLicense = useSelector(selectRiskScoreFeatureFlag);
  const canUpdateCvss = useSelector((state) => selectAllowedByRole(state, FEATURE_VULNS, PERMISSION_TYPES.UPDATE));

  const vulnCvss = () => {
    if (!currentVuln?.cvss2?.vector_string && !currentVuln?.cvss3?.vector_string && !currentVuln?.cvss4?.vector_string) return;
    if (currentVuln?.cvss4?.vector_string) return { ...currentVuln.cvss4, isV3: false, isV4: true };
    else if (currentVuln?.cvss3?.vector_string && !currentVuln?.cvss4?.vector_string) return { ...currentVuln.cvss3, isV3: true, isV4: false };
    else if (currentVuln?.cvss2?.vector_string && !currentVuln?.cvss3?.vector_string && !currentVuln?.cvss4?.vector_string) return { ...currentVuln.cvss2, isV3: false, isV4: false };
    else return null;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const cvss = useMemo(() => vulnCvss(), [currentVuln.cvss3?.vector_string, currentVuln.cvss2?.vector_string, currentVuln.cvss4?.vector_string]);
  const cwe = useMemo(() => (currentVuln.cwe || []).filter(Boolean), [currentVuln.cwe]);
  const owasp = useMemo(() => currentVuln.owasp[0], [currentVuln.owasp]);
  const risk = useMemo(() => currentVuln.risk, [currentVuln.risk]);

  const hasRiskInfo = cvss || cwe.length > 0 || owasp || (risk?.score !== 0);
  const title = (hasRiskInfo || canUpdateCvss) ? intl.formatMessage({ id: 'manage.detail.tab.general.risk' }) : intl.formatMessage({ id: 'manage.detail.tab.general.cve' });
  const hasExploitable = currentVuln.tags.includes('exploit-available');
  const hasPatch = currentVuln.tags.includes('patch-available');
  const showRiskInfo = hasRiskInfo || canUpdateCvss;

  const cvssText = () => {
    if (!cvss) return '';
    else if (cvss && cvss.isV3 && !cvss.isV4) return 'v3.1';
    else if (cvss && !cvss.isV3 && cvss.isV4) return 'v4.0';
    else return 'v2.0';
  };

  const TopButtons = ({ loading }) => (
    <ButtonsWrapper>
      {loading
        ? (
        <SpinnerComponent size="16px" />
          )
        : (
        <IconButton title="Refresh" onClick={ () => dispatch(refreshVulnEnrichment(currentVuln)) }>
          <RefreshIcon />
        </IconButton>
          )}
      {/* <Link target="_blank" href="" rel="noopener noreferrer" tooltip="" ><HelpIcon /></Link> */}
    </ButtonsWrapper>
  );

  return (
    <ExpandableWrapper id={ ID } title={ title } defaultValue={ isExpanded } tab={ TAB } buttons={ (hasRiskInfo && isAllowedByLicense) ? <TopButtons loading={ fetchingVulnEnrichment } /> : null }>
     { hasRiskInfo &&
      <Risk>
        <table>
        {((risk.score !== 0) && isAllowedByLicense)
          ? <Line>
            <Title>Risk Score</Title>
            <RiskScoreInfo>
              <RiskScore color={ risk.severity }>{risk.score} ({risk.severity})</RiskScore>
              {hasExploitable && <RefTag onClick={ scrollToReferences }>Exploitable</RefTag>}
              {hasPatch && <RefTag onClick={ scrollToReferences } green>Patchable</RefTag>}
            </RiskScoreInfo>
            </Line>
          : null}
        <Line>
            <Text pb bold>CVSS { cvssText() } Base Score</Text>
            <Text pb bold color={ cvss && cvss.base_severity }>{(cvss && cvss.base_score) ? cvss.base_score : 'N/A' }</Text>
        </Line>
        {cwe.length > 0 && <Line>
            <Text pb bold>CWE Weakness Type</Text>
            <Text pb>
              {cwe.map((c, i) => (
                <React.Fragment key={ c }>
                  <Link target="_blank" href={ `https://cwe.mitre.org/data/definitions/${c.replace('CWE-', '').replace('cwe-', '')}.html` } rel="noopener noreferrer">{c}</Link>
                  {i < cwe.length - 1 && ', '}
                </React.Fragment>
              ))}
            </Text>
                           </Line>}
        {(owasp && isAllowedByLicense) && <Line>
            <Text bold>OWASP Category</Text>
            <Text>{owasp || 'No owasp'}</Text>
                                          </Line>}
        </table>

      </Risk>}
      <CVE showRiskInfo={ showRiskInfo } currentVuln={ currentVuln } updateVuln={ updateVuln } />
      <CWE currentVuln={ currentVuln } />
      { isAllowedByLicense && <CVSS showRiskInfo={ showRiskInfo } currentVuln={ currentVuln } updateVuln={ updateVuln } tempCVSS={ tempCVSS } resetCVSS={ resetCVSS } entity={ entity } /> }
    </ExpandableWrapper>
  );
};

export default RiskAndCVE;
